import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { addDays, format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { canAccessDigitalTwinDemo } from '../config/digitalTwinDemoAccess';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
} from 'recharts';
import {
  ArrowLeft,
  RefreshCw,
  CalendarDays,
  Activity,
  Shield,
  HardHat,
  ChevronRight,
  LayoutGrid,
  Sparkles,
  Table2,
  TrendingUp,
  Bot,
  Shuffle,
  Loader2,
  Network,
} from 'lucide-react';
import {
  KG_NODE_TYPES,
  SAMSUNG_KG_NODES,
  SAMSUNG_KG_EDGES,
  SAMSUNG_KG_SAMPLE_QUESTIONS,
  buildKgChatPayload,
} from '../data/samsungFabKnowledgeGraph';
import {
  DIGITAL_TWIN_PROJECTS,
  getProjectTwinData,
  getQuarterlySummaries,
  compareQuarters,
  buildFinancialTwinInsights,
  buildOpsTwinInsights,
  buildScheduleTwinInsights,
  buildSafetyTwinInsights,
  buildTwinAgentSnapshot,
} from '../data/digitalTwinProjects';

const COLORS = {
  bg: 'min-h-full bg-gray-50 text-gray-900 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8',
  card: 'rounded-2xl border border-gray-200 bg-white shadow-sm',
  accent: 'text-teal-700',
  warn: 'text-amber-700',
  muted: 'text-gray-600',
};

const PIE_COLORS = ['#f97316', '#ef4444', '#14b8a6'];

function formatMonthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  const short = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1];
  return `${short}-${String(y).slice(-2)}`;
}

function quarterSortKey(k) {
  const [ys, qs] = k.split('-Q');
  return Number(ys) * 4 + Number(qs);
}

function quarterBlendTag(blend) {
  if (blend === 'projected') return ' · projected';
  if (blend === 'mixed') return ' · mixed actual/proj';
  return '';
}

const SAMPLE_TWIN_AGENT_QUERIES = [
  'Build a view focused on predicted 2026 Q3 vs Q4 burn and billings using projected quarters.',
  'Show CPI and SPI monthly trend with financial health KPIs — how efficient is execution?',
  'Executive snapshot: revised contract, cost to date, margin %, and composite health.',
  'Compare three projected 2027 quarters on quarterly cost and percent complete.',
  'Ops story: relate RFIs, submittals, and COs to the heaviest burn quarters.',
  'Safety + schedule: SOAR observations, TRIR, days since incident, and SPI over recent months.',
  'Highlight forecast curve: monthly cost and % complete including projected twin months.',
];

const TWIN_KPI_LABELS = {
  revisedContractM: 'Revised contract ($M)',
  contractOriginalM: 'Original contract ($M)',
  approvedCOM: 'Approved CO ($M)',
  costToDateM: 'Cost to date ($M)',
  estRemainingM: 'ETC ($M)',
  pctComplete: '% complete',
  estProfitM: 'Est. profit ($M)',
  estGrossMarginPct: 'Est. margin %',
  balanceToBillM: 'Balance to bill ($M)',
  billedToDateM: 'Billed to date ($M)',
  wipM: 'WIP ($M)',
  financial: 'Financial health',
  operations: 'Operations health',
  schedule: 'Schedule health',
  safety: 'Safety health',
  composite: 'Composite health',
};

const TWIN_HEALTH_KEYS = new Set(['financial', 'operations', 'schedule', 'safety', 'composite']);

function twinAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

function sliceMonthlyForAgent(snapshot, monthSpan, includeProjected) {
  const raw = snapshot.monthlySeries || [];
  const pool = includeProjected ? raw : raw.filter((r) => !r.isProjected);
  return pool.slice(-monthSpan).map((r) => ({
    label: formatMonthLabel(r.month),
    ...r,
  }));
}

function formatTwinKpiDisplay(key, kpis, health) {
  const label = TWIN_KPI_LABELS[key] || key;
  let val;
  if (TWIN_HEALTH_KEYS.has(key)) val = health?.[key];
  else val = kpis?.[key];
  if (val == null || Number.isNaN(val)) return { label, text: '—' };
  if (key === 'pctComplete' || key === 'estGrossMarginPct') return { label, text: `${val}%` };
  if (TWIN_HEALTH_KEYS.has(key)) return { label, text: `${Math.round(val)}/100` };
  return { label, text: typeof val === 'number' ? String(val) : String(val) };
}

const LINE_METRIC_COLORS = ['#2dd4bf', '#fb923c', '#a78bfa', '#f472b6'];

/** @param {{ plan: object, snapshot: object }} props */
function TwinAgentWidgets({ plan, snapshot }) {
  if (!plan?.widgets?.length) return null;
  const kpis = snapshot.kpis || {};
  const health = snapshot.health || {};

  return (
    <div className="space-y-6 mt-4">
      {plan.widgets.map((w, i) => {
        if (w.type === 'kpi_strip') {
          return (
            <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              {w.title && (
                <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-3">{w.title}</p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {w.keys.map((key) => {
                  const { label, text } = formatTwinKpiDisplay(key, kpis, health);
                  return (
                    <div key={key} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
                      <p className="text-lg font-semibold text-gray-900 font-mono mt-1">{text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        if (w.type === 'line_monthly') {
          const rows = sliceMonthlyForAgent(snapshot, w.monthSpan || 14, Boolean(w.includeProjected));
          const metrics = w.metrics || [];
          const needsRightAxis = metrics.includes('pctComplete') && metrics.some((m) => m !== 'pctComplete');
          return (
            <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              {w.title && (
                <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">{w.title}</p>
              )}
              <p className="text-[10px] text-gray-500 mb-3">
                {w.includeProjected ? 'Includes projected twin months (violet tick = forecast).' : 'Actual months only.'}
              </p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={rows} margin={{ top: 8, right: needsRightAxis ? 16 : 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 9 }} interval={Math.max(0, Math.floor(rows.length / 8))} />
                    <YAxis yAxisId="left" tick={{ fill: '#6b7280', fontSize: 10 }} />
                    {needsRightAxis && (
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: '#6b7280', fontSize: 10 }} domain={[0, 100]} />
                    )}
                    <Tooltip
                      contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 11, color: '#111827' }}
                      formatter={(v, name) => [v, name]}
                      labelFormatter={(_, p) => (p?.[0]?.payload?.isProjected ? `${p[0].payload.label} (projected)` : p?.[0]?.payload?.label)}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#374151' }} />
                    {metrics.map((m, mi) => (
                      <Line
                        key={m}
                        yAxisId={m === 'pctComplete' && needsRightAxis ? 'right' : 'left'}
                        type="monotone"
                        dataKey={m}
                        name={m}
                        stroke={LINE_METRIC_COLORS[mi % LINE_METRIC_COLORS.length]}
                        strokeWidth={2}
                        dot={(props) => {
                          const proj = props.payload?.isProjected;
                          if (proj) return <circle cx={props.cx} cy={props.cy} r={3} fill="#c4b5fd" stroke="#7c3aed" />;
                          return <circle cx={props.cx} cy={props.cy} r={2} fill={LINE_METRIC_COLORS[mi % LINE_METRIC_COLORS.length]} />;
                        }}
                      />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        }

        if (w.type === 'bar_quarters') {
          const qMap = new Map((snapshot.quarters || []).map((q) => [q.quarterKey, q]));
          const chartRows = (w.quarterKeys || [])
            .map((qk) => {
              const q = qMap.get(qk);
              if (!q) return null;
              return {
                label: qk.replace('-Q', ' ·Q'),
                value: q[w.field] ?? 0,
                blend: q.blend,
              };
            })
            .filter(Boolean);
          const fieldLabel = w.field || 'costM';
          return (
            <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              {w.title && (
                <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-3">{w.title}</p>
              )}
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartRows}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 9 }} />
                    <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 11, color: '#111827' }}
                      formatter={(v, _n, item) => [`${v}${fieldLabel === 'endPctComplete' ? '%' : ''}`, fieldLabel]}
                      labelFormatter={(lab, payload) => {
                        const b = payload?.[0]?.payload?.blend;
                        return b ? `${lab} (${b})` : lab;
                      }}
                    />
                    <Bar dataKey="value" name={fieldLabel} radius={[6, 6, 0, 0]}>
                      {chartRows.map((entry, j) => (
                        <Cell
                          key={j}
                          fill={
                            entry.blend === 'projected' ? '#7c3aed' : entry.blend === 'mixed' ? '#ca8a04' : '#64748b'
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

const INSIGHT_TONE = {
  positive: 'border-teal-200 bg-teal-50',
  risk: 'border-red-200 bg-red-50',
  neutral: 'border-gray-200 bg-gray-50',
};

function InsightStrip({ title, executive, bullets }) {
  return (
    <div className="mt-4 space-y-3 border-t border-gray-200 pt-4">
      <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 shrink-0" />
        {title}
      </p>
      <p className="text-xs text-gray-700 leading-relaxed">{executive}</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {bullets.map((b, i) => (
          <div
            key={i}
            className={`rounded-lg border px-2.5 py-2 text-[11px] leading-snug ${INSIGHT_TONE[b.tone] || INSIGHT_TONE.neutral}`}
          >
            <p className="font-semibold text-gray-900 text-xs">{b.title}</p>
            <p className="text-gray-600 mt-1">{b.detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function QoQInsightsRich({ insights }) {
  if (!insights) return null;
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white px-4 py-4">
        <p className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wider">{insights.verdictLabel}</p>
        <h3 className="text-xl font-bold text-gray-900 mt-2">{insights.headline}</h3>
        <p className="text-sm text-gray-600 mt-1">{insights.subhead}</p>
        {insights.forecastNote && (
          <p className="text-xs text-violet-800 mt-3 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 leading-relaxed">
            {insights.forecastNote}
          </p>
        )}
        {insights.meta?.costIntensity != null && (
          <p className="text-xs text-gray-500 mt-3 font-mono">
            Cost intensity ratio (compare ÷ baseline): {insights.meta.costIntensity}
            {insights.meta.progressEfficiency != null && (
              <span className="ml-3">
                · ~${Math.abs(insights.meta.progressEfficiency)}M per pt % complete (directional)
              </span>
            )}
          </p>
        )}
      </div>

      <div>
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-amber-600" />
          Executive read
        </p>
        <p className="text-sm text-gray-800 leading-relaxed">{insights.executiveSummary}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {insights.highlights.map((h, i) => (
          <div
            key={i}
            className={`rounded-xl border px-3 py-3 ${INSIGHT_TONE[h.tone] || INSIGHT_TONE.neutral}`}
          >
            <p className="text-sm font-semibold text-gray-900">{h.title}</p>
            <p className="text-xs text-gray-600 mt-2 leading-relaxed">{h.detail}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-[11px] font-semibold text-amber-900 uppercase tracking-wide mb-2">
          Suggested next actions
        </p>
        <ul className="text-xs text-gray-700 space-y-2 list-disc pl-4 leading-relaxed">
          {insights.actions.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * Interactive knowledge-graph panel — Samsung Taylor fab from public-record facts.
 * SVG graph (click any node), plus free-form Q&A grounded ONLY in the graph via
 * POST /api/digital-twin-dashboard/kg-chat. Answer nodeIds light up the traversal.
 */
function KnowledgeGraphPanel({ user }) {
  const [selectedId, setSelectedId] = useState('fab');
  const [kgQuery, setKgQuery] = useState('');
  const [kgAnswer, setKgAnswer] = useState(null);
  const [kgLoading, setKgLoading] = useState(false);
  const [kgError, setKgError] = useState('');
  const [highlightIds, setHighlightIds] = useState(new Set());

  const nodeById = useMemo(() => new Map(SAMSUNG_KG_NODES.map((n) => [n.id, n])), []);
  const selected = nodeById.get(selectedId);
  const selectedEdges = useMemo(
    () => SAMSUNG_KG_EDGES.filter((e) => e.from === selectedId || e.to === selectedId),
    [selectedId],
  );

  const askKg = useCallback(
    async (q) => {
      const query = (q ?? kgQuery).trim();
      if (query.length < 3) {
        setKgError('Type a question (at least a few characters).');
        return;
      }
      setKgLoading(true);
      setKgError('');
      try {
        const res = await axios.post(
          '/api/digital-twin-dashboard/kg-chat',
          { query, graph: buildKgChatPayload(), loginEmail: user?.email },
          { withCredentials: true, ...twinAuthHeaders() },
        );
        if (!res.data?.ok) {
          setKgError(res.data?.error || 'Unexpected response');
          setKgAnswer(null);
          setHighlightIds(new Set());
        } else {
          setKgAnswer({ question: query, answer: res.data.answer, trail: res.data.trail || [], model: res.data.model });
          setHighlightIds(new Set(res.data.nodeIds || []));
          if (res.data.nodeIds?.length) setSelectedId(res.data.nodeIds[0]);
        }
      } catch (err) {
        setKgError(err.response?.data?.error || err.message || 'Request failed');
        setKgAnswer(null);
        setHighlightIds(new Set());
      } finally {
        setKgLoading(false);
      }
    },
    [kgQuery, user?.email],
  );

  const randomKgQuestion = useCallback(() => {
    const pick = SAMSUNG_KG_SAMPLE_QUESTIONS[Math.floor(Math.random() * SAMSUNG_KG_SAMPLE_QUESTIONS.length)];
    setKgQuery(pick);
    setKgError('');
  }, []);

  const edgeIsLit = useCallback(
    (e) =>
      (highlightIds.has(e.from) && highlightIds.has(e.to)) ||
      e.from === selectedId ||
      e.to === selectedId,
    [highlightIds, selectedId],
  );

  return (
    <div className="space-y-4">
      <div className={`${COLORS.card} p-5`}>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Network className="w-5 h-5 text-teal-700" />
              Knowledge graph — Taylor fab, from the public record
            </h2>
            <p className="text-xs text-gray-500 mt-1 max-w-3xl leading-relaxed">
              Areas, systems, events and milestones extracted from public announcements and reporting — every edge
              cites its source, the same way the production graph API returns a traversal trail. Grey nodes are
              synthetic execution records showing how a live project attaches to the same graph (the digital twin on
              the other tabs is one of them). Click any node, or ask a free-form question below.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600 shrink-0">
            {Object.entries(KG_NODE_TYPES).map(([k, t]) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <i className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: t.color }} />
                {t.label}
              </span>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
            <svg viewBox="0 0 1000 640" className="w-full h-auto" role="img" aria-label="Taylor fab knowledge graph">
              {SAMSUNG_KG_EDGES.map((e, i) => {
                const a = nodeById.get(e.from);
                const b = nodeById.get(e.to);
                if (!a || !b) return null;
                const lit = edgeIsLit(e);
                return (
                  <g key={i}>
                    <line
                      x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                      stroke={lit ? '#0d9488' : '#cbd5e1'}
                      strokeWidth={lit ? 2 : 1}
                      strokeDasharray={e.rel === 'referenced_in' ? '4 3' : undefined}
                    />
                    {lit && (
                      <text
                        x={(a.x + b.x) / 2}
                        y={(a.y + b.y) / 2 - 4}
                        textAnchor="middle"
                        fontSize="10"
                        fill="#0f766e"
                        fontStyle="italic"
                      >
                        {e.rel}
                      </text>
                    )}
                  </g>
                );
              })}
              {SAMSUNG_KG_NODES.map((n) => {
                const c = KG_NODE_TYPES[n.type]?.color || '#64748b';
                const isSel = n.id === selectedId;
                const isHi = highlightIds.has(n.id);
                const r = n.type === 'project' ? 20 : 12;
                return (
                  <g
                    key={n.id}
                    onClick={() => setSelectedId(n.id)}
                    className="cursor-pointer"
                    role="button"
                    aria-label={n.label}
                  >
                    {(isSel || isHi) && (
                      <circle cx={n.x} cy={n.y} r={r + 6} fill="none" stroke={isHi ? '#f59e0b' : '#0d9488'} strokeWidth={2.5} opacity={0.85} />
                    )}
                    <circle cx={n.x} cy={n.y} r={r} fill={c} opacity={n.type === 'record' ? 0.75 : 0.95} />
                    <text
                      x={n.x}
                      y={n.y + r + 13}
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight={isSel || isHi ? 700 : 500}
                      fill="#334155"
                    >
                      {n.label}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          <div className="space-y-3">
            {selected && (
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-2">
                  <i
                    className="w-3 h-3 rounded-full inline-block shrink-0"
                    style={{ background: KG_NODE_TYPES[selected.type]?.color }}
                  />
                  <p className="text-sm font-semibold text-gray-900">{selected.label}</p>
                </div>
                <p className="text-[10px] uppercase tracking-wide text-gray-500 mt-1">
                  {KG_NODE_TYPES[selected.type]?.label}
                </p>
                <p className="text-xs text-gray-700 mt-2 leading-relaxed">{selected.detail}</p>
                {selectedEdges.length > 0 && (
                  <div className="mt-3 border-t border-gray-100 pt-2 space-y-1.5">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Edges (cited)</p>
                    {selectedEdges.map((e, i) => (
                      <p key={i} className="text-[11px] text-gray-600 leading-snug">
                        <button
                          type="button"
                          className="text-teal-700 hover:underline"
                          onClick={() => setSelectedId(e.from === selectedId ? e.to : e.from)}
                        >
                          {nodeById.get(e.from === selectedId ? e.to : e.from)?.label}
                        </button>{' '}
                        <span className="italic text-gray-400">{e.from === selectedId ? `←[${e.rel}]` : `—[${e.rel}]→`}</span>
                        <span className="block text-[10px] text-gray-400">{e.source}</span>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`${COLORS.card} p-5`}>
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-teal-700" />
          Ask the graph anything
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Free-form questions, answered ONLY from the graph above — with the node trail lit up and sources cited.
        </p>

        <div className="flex flex-wrap gap-2 mt-3 mb-3">
          {SAMSUNG_KG_SAMPLE_QUESTIONS.slice(0, 4).map((sq) => (
            <button
              key={sq}
              type="button"
              onClick={() => { setKgQuery(sq); setKgError(''); }}
              className="text-left text-[11px] px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:border-teal-500 hover:text-teal-800 transition-colors max-w-[15rem] leading-snug"
            >
              {sq}
            </button>
          ))}
          <button
            type="button"
            onClick={randomKgQuestion}
            className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg border border-teal-300 text-teal-800 hover:bg-teal-50 transition-colors"
          >
            <Shuffle className="w-3.5 h-3.5" />
            Random question
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <label className="flex-1 block">
            <span className="sr-only">Knowledge graph question</span>
            <textarea
              value={kgQuery}
              onChange={(e) => setKgQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askKg(); }
              }}
              rows={2}
              placeholder="e.g. Why did mass production move to 2026? What gates tool move-in?"
              className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 resize-y min-h-[3.5rem]"
            />
          </label>
          <button
            type="button"
            onClick={() => askKg()}
            disabled={kgLoading}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-teal-600 hover:bg-teal-500 disabled:opacity-45 text-white text-sm font-medium shrink-0"
          >
            {kgLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
            Ask the graph
          </button>
        </div>

        {kgError ? (
          <p className="mt-3 text-sm text-amber-900 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">{kgError}</p>
        ) : null}

        {kgAnswer ? (
          <div className="mt-4 rounded-xl border border-teal-200 bg-teal-50/60 px-4 py-3">
            <p className="text-[11px] uppercase tracking-wide text-teal-800 font-semibold">Q · {kgAnswer.question}</p>
            <p className="text-sm text-gray-800 mt-2 leading-relaxed">{kgAnswer.answer}</p>
            {kgAnswer.trail.length > 0 && (
              <div className="mt-3 border-t border-teal-200/70 pt-2">
                <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Traversal trail</p>
                {kgAnswer.trail.map((t, i) => (
                  <p key={i} className="text-[11px] font-mono text-gray-600 leading-relaxed">{t}</p>
                ))}
              </div>
            )}
            {kgAnswer.model ? (
              <p className="text-[10px] text-gray-400 mt-2 font-mono">Answered by {kgAnswer.model} · grounded in the graph only</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function DigitalTwinDemo() {
  const { user } = useAuth();
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const [projectId, setProjectId] = useState(DIGITAL_TWIN_PROJECTS[0].id);
  const [asOf, setAsOf] = useState(todayStr);
  const [qBase, setQBase] = useState('');
  const [qCompare, setQCompare] = useState('');
  /** @type {'insights' | 'dashboard' | 'table'} */
  const [qoqTab, setQoqTab] = useState('insights');
  /** @type {'daily' | 'qoq' | 'nl'} */
  const [mainTab, setMainTab] = useState('daily');

  const [agentQuery, setAgentQuery] = useState('');
  const [agentPlan, setAgentPlan] = useState(null);
  const [agentSnapshot, setAgentSnapshot] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState('');
  const [agentModel, setAgentModel] = useState('');

  const data = useMemo(() => getProjectTwinData(projectId, asOf), [projectId, asOf]);
  const actualMonthly = useMemo(() => data.monthly.filter((r) => !r.isProjected), [data.monthly]);
  const quarters = useMemo(() => getQuarterlySummaries(projectId, asOf), [projectId, asOf]);
  useEffect(() => {
    setAgentPlan(null);
    setAgentSnapshot(null);
    setAgentError('');
  }, [projectId, asOf]);

  const finInsights = useMemo(() => buildFinancialTwinInsights(data), [data]);
  const opsInsights = useMemo(() => buildOpsTwinInsights(data), [data]);
  const scheduleInsights = useMemo(() => buildScheduleTwinInsights(data), [data]);
  const safetyInsights = useMemo(() => buildSafetyTwinInsights(data), [data]);

  const scheduleSpotlight = useMemo(() => {
    const last6 = actualMonthly.slice(-6);
    return {
      latestEv: actualMonthly[actualMonthly.length - 1],
      avgSpi6: last6.length ? last6.reduce((s, r) => s + r.spi, 0) / last6.length : null,
    };
  }, [actualMonthly]);

  React.useEffect(() => {
    if (quarters.length < 2) return;
    const keySet = new Set(quarters.map((q) => q.quarterKey));
    if (!keySet.has(qBase) || !keySet.has(qCompare) || qBase === qCompare) {
      setQBase(quarters[quarters.length - 2].quarterKey);
      setQCompare(quarters[quarters.length - 1].quarterKey);
    }
  }, [projectId, asOf, quarters, qBase, qCompare]);

  const financialComboData = useMemo(
    () =>
      actualMonthly.slice(-14).map((r) => ({
        label: formatMonthLabel(r.month),
        costM: r.costMonthM,
        billedCumulativeM: r.billedCumulativeM,
      })),
    [actualMonthly],
  );

  const maxBilledCumulative = useMemo(() => {
    const vals = financialComboData.map((d) => d.billedCumulativeM);
    return vals.length ? Math.max(...vals, 1) : 1;
  }, [financialComboData]);

  const opsDocTrendData = useMemo(
    () =>
      actualMonthly.slice(-14).map((r) => ({
        label: formatMonthLabel(r.month),
        docIndex: r.docActivityIndex,
      })),
    [actualMonthly],
  );

  const perfChartData = useMemo(
    () =>
      actualMonthly.slice(-12).map((r) => ({
        label: formatMonthLabel(r.month),
        cpi: r.cpi,
        spi: r.spi,
      })),
    [actualMonthly],
  );

  const opsChartData = useMemo(
    () => [
      { name: 'RFIs (merged)', value: data.ops.rfiMerged },
      { name: 'Submittals', value: data.ops.submittalsMerged },
      { name: 'Change orders', value: data.ops.changeOrdersMerged },
    ],
    [data.ops],
  );

  const safetyPie = useMemo(
    () => [
      { name: 'OSHA recordable', value: data.safety.oshaRecordable },
      { name: 'First aid only', value: data.safety.firstAidOnly },
      {
        name: data.safety.lostTimeDays > 0 ? 'Lost time (LTI)' : 'Zero lost time',
        value: Math.max(1, 12 - data.safety.oshaRecordable - data.safety.firstAidOnly),
      },
    ],
    [data.safety],
  );

  const comparison = useMemo(() => {
    if (!qBase || !qCompare || qBase === qCompare) return null;
    const ordered = [qBase, qCompare].sort((a, b) => quarterSortKey(a) - quarterSortKey(b));
    return compareQuarters(projectId, asOf, ordered[0], ordered[1]);
  }, [projectId, asOf, qBase, qCompare]);

  const qoqDashboard = useMemo(() => {
    if (!comparison?.ok) return null;
    const { a, b } = comparison;
    const label = (qk) => qk.replace('-Q', ' · Q');
    const monthsA = data.monthly.filter((m) => m.quarterKey === a.quarterKey);
    const monthsB = data.monthly.filter((m) => m.quarterKey === b.quarterKey);
    const maxLen = Math.max(monthsA.length, monthsB.length);
    const burnProfile = [];
    for (let i = 0; i < maxLen; i += 1) {
      burnProfile.push({
        slot: `Month ${i + 1}`,
        baseline: monthsA[i]?.costMonthM ?? null,
        compare: monthsB[i]?.costMonthM ?? null,
      });
    }
    const fillA = a.blend === 'projected' ? '#6d28d9' : a.blend === 'mixed' ? '#a16207' : '#64748b';
    const fillB = b.blend === 'projected' ? '#c4b5fd' : b.blend === 'mixed' ? '#fde047' : '#2dd4bf';
    const billA = a.blend === 'projected' ? '#5b21b6' : a.blend === 'mixed' ? '#854d0e' : '#64748b';
    const billB = b.blend === 'projected' ? '#ddd6fe' : b.blend === 'mixed' ? '#fef08a' : '#22d3ee';

    return {
      baseline: a,
      compare: b,
      burnProfile,
      costData: [
        { quarter: label(a.quarterKey), costM: a.costM, fill: fillA },
        { quarter: label(b.quarterKey), costM: b.costM, fill: fillB },
      ],
      billedData: [
        { quarter: label(a.quarterKey), billedM: a.billedQuarterM, fill: billA },
        { quarter: label(b.quarterKey), billedM: b.billedQuarterM, fill: billB },
      ],
      wipData: [
        { quarter: label(a.quarterKey), wipM: a.endWipM },
        { quarter: label(b.quarterKey), wipM: b.endWipM },
      ],
      volatilityData: [
        { quarter: label(a.quarterKey), sigma: a.costVolatility },
        { quarter: label(b.quarterKey), sigma: b.costVolatility },
      ],
      perfData: [
        { quarter: label(a.quarterKey), CPI: a.avgCpi, SPI: a.avgSpi },
        { quarter: label(b.quarterKey), CPI: b.avgCpi, SPI: b.avgSpi },
      ],
      progressData: [
        { quarter: label(a.quarterKey), pctComplete: a.endPctComplete },
        { quarter: label(b.quarterKey), pctComplete: b.endPctComplete },
      ],
      healthData: [
        { quarter: label(a.quarterKey), score: a.compositeHint },
        { quarter: label(b.quarterKey), score: b.compositeHint },
      ],
      opsDocsData: [
        {
          quarter: label(a.quarterKey),
          RFIs: a.rfiQuarter,
          Submittals: a.submittalsQuarter,
          COs: a.changeOrdersQuarter,
        },
        {
          quarter: label(b.quarterKey),
          RFIs: b.rfiQuarter,
          Submittals: b.submittalsQuarter,
          COs: b.changeOrdersQuarter,
        },
      ],
      burnEfficiencyData: [
        {
          quarter: label(a.quarterKey),
          burnPerPct: a.burnPerPctPt ?? 0,
        },
        {
          quarter: label(b.quarterKey),
          burnPerPct: b.burnPerPctPt ?? 0,
        },
      ],
      safetyCultureData: [
        {
          quarter: label(a.quarterKey),
          observations: a.obsDelta,
          incidents: a.incidentDelta,
        },
        {
          quarter: label(b.quarterKey),
          observations: b.obsDelta,
          incidents: b.incidentDelta,
        },
      ],
    };
  }, [comparison, data.monthly]);

  const advanceDay = useCallback(() => {
    const d = addDays(new Date(`${asOf}T12:00:00`), 1);
    setAsOf(format(d, 'yyyy-MM-dd'));
  }, [asOf]);

  const runTwinDashboardAgent = useCallback(async () => {
    const q = agentQuery.trim();
    if (q.length < 3) {
      setAgentError('Type a short question (at least 3 characters).');
      return;
    }
    setAgentLoading(true);
    setAgentError('');
    const snapshot = buildTwinAgentSnapshot(projectId, asOf);
    try {
      const res = await axios.post(
        '/api/digital-twin-dashboard/agent-plan',
        { query: q, snapshot, loginEmail: user?.email },
        { withCredentials: true, ...twinAuthHeaders() },
      );
      if (!res.data?.ok || !res.data.plan) {
        setAgentError(res.data?.error || 'Unexpected response');
        setAgentPlan(null);
        setAgentSnapshot(null);
      } else {
        setAgentPlan(res.data.plan);
        setAgentSnapshot(snapshot);
        setAgentModel(res.data.model || '');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Request failed';
      setAgentError(msg);
      setAgentPlan(null);
      setAgentSnapshot(null);
    } finally {
      setAgentLoading(false);
    }
  }, [agentQuery, projectId, asOf, user?.email]);

  const surpriseTwinQuery = useCallback(() => {
    const pick = SAMPLE_TWIN_AGENT_QUERIES[Math.floor(Math.random() * SAMPLE_TWIN_AGENT_QUERIES.length)];
    setAgentQuery(pick);
    setAgentError('');
  }, []);

  const k = data.kpis;

  return (
    <div className={COLORS.bg}>
      <div className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <Link
              to="/jobotics-agent"
              className="mt-1 inline-flex items-center gap-1 text-sm text-teal-700 hover:text-teal-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Jobotics Agent
            </Link>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Demo dataset · Project heatmap</p>
              <h1 className="text-2xl font-bold text-gray-900 mt-1">
                {data.meta.code} — {data.meta.name}
              </h1>
              <p className="text-sm text-gray-600 mt-1">{data.meta.subtitle}</p>
              <p className="text-xs text-gray-500 mt-2">
                PM {data.meta.pm} · {data.meta.division} · {data.meta.company}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className={`${COLORS.card} px-3 py-2 flex items-center gap-2`}>
              <label className="text-xs text-gray-600 flex items-center gap-1">
                <CalendarDays className="w-3.5 h-3.5" />
                As of
              </label>
              <input
                type="date"
                value={asOf}
                max="2027-12-31"
                min="2024-01-01"
                onChange={(e) => setAsOf(e.target.value)}
                className="bg-white border border-gray-300 text-gray-900 rounded-lg px-2 py-1 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={advanceDay}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Daily refresh
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-gray-500 self-center mr-2">Projects (2024–2027)</span>
          {DIGITAL_TWIN_PROJECTS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setProjectId(p.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                projectId === p.id
                  ? 'bg-teal-100 border-teal-600 text-teal-900 font-semibold shadow-sm'
                  : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
              }`}
            >
              {p.code}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
          {[
            { id: 'daily', label: 'Daily snapshot', short: 'Snapshot', Icon: CalendarDays },
            { id: 'qoq', label: 'Quarter-to-quarter', short: 'Q/Q', Icon: TrendingUp },
            { id: 'nl', label: 'Natural language query', short: 'NL', Icon: Bot },
            { id: 'kg', label: 'Knowledge graph', short: 'KG', Icon: Network },
          ].map(({ id, label, short, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setMainTab(id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                mainTab === id
                  ? 'bg-teal-100 border-teal-600 text-teal-900 font-semibold shadow-sm ring-2 ring-teal-600/25'
                  : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{short}</span>
            </button>
          ))}
        </div>

        {mainTab === 'daily' && (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {[
            {
              label: 'Revised contract',
              value: `$${k.revisedContractM}M`,
              hint: `Original $${k.contractOriginalM}M + $${k.approvedCOM}M CO`,
            },
            {
              label: 'Cost to date',
              value: `$${k.costToDateM}M`,
              hint: `ETC ~ $${k.estRemainingM}M`,
            },
            {
              label: '% complete',
              value: `${k.pctComplete}%`,
              hint: 'SOC-style progress',
            },
            {
              label: 'Est. profit @ completion',
              value: `$${k.estProfitM}M`,
              hint: `${k.estGrossMarginPct}% margin`,
            },
            {
              label: 'Balance to bill',
              value: `$${k.balanceToBillM}M`,
              hint: `Billed $${k.billedToDateM}M`,
            },
            {
              label: 'Composite health',
              value: `${data.health.composite}/100`,
              hint: 'Fin · Ops · Schedule · Safety',
            },
          ].map((card) => (
            <div key={card.label} className={`${COLORS.card} p-4`}>
              <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{card.label}</p>
              <p className="text-xl font-bold text-gray-900 mt-2">{card.value}</p>
              <p className="text-[11px] text-gray-500 mt-2 leading-snug">{card.hint}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className={`${COLORS.card} p-5`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-teal-700" />
                  Financial performance
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Monthly burn vs billed cumulative · Vista / SOC bridge · synthetic twin
                </p>
              </div>
              <span className="text-teal-700 text-sm font-mono">{data.health.financial}/100</span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={financialComboData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 9 }} interval={0} angle={-35} textAnchor="end" height={52} />
                  <YAxis
                    yAxisId="left"
                    tick={{ fill: '#6b7280', fontSize: 10 }}
                    label={{ value: '$M cost / mo', angle: -90, position: 'insideLeft', fill: '#9ca3af', fontSize: 10 }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[0, Math.ceil(maxBilledCumulative * 1.08)]}
                    tick={{ fill: '#6b7280', fontSize: 10 }}
                    label={{ value: 'Billed cumulative $M', angle: 90, position: 'insideRight', fill: '#9ca3af', fontSize: 10 }}
                  />
                  <Tooltip
                    contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }}
                    labelStyle={{ color: '#374151' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#374151' }} />
                  <Bar yAxisId="left" dataKey="costM" fill="#2dd4bf" radius={[3, 3, 0, 0]} name="Monthly cost ($M)" />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="billedCumulativeM"
                    stroke="#38bdf8"
                    strokeWidth={2}
                    dot={false}
                    name="Billed cumulative ($M)"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] border-t border-gray-200 pt-3">
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">Vista actual</p>
                <p className="text-gray-900 font-mono">${data.crossCheck.vistaActualM}M</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">SOC JTD</p>
                <p className="text-gray-900 font-mono">${data.crossCheck.socJtdM}M</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">Timing Δ</p>
                <p className="text-amber-800 font-mono">${data.crossCheck.timingDeltaM}M</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">WIP proxy</p>
                <p className="text-gray-900 font-mono">${k.wipM}M</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">Est. margin</p>
                <p className="text-emerald-700 font-mono">{k.estGrossMarginPct}%</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">Balance to bill</p>
                <p className="text-gray-900 font-mono">${k.balanceToBillM}M</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">ETC (est.)</p>
                <p className="text-gray-900 font-mono">${k.estRemainingM}M</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">Approved CO</p>
                <p className="text-gray-900 font-mono">${data.crossCheck.approvedCO}M</p>
              </div>
            </div>
            <InsightStrip title="Financial insights" executive={finInsights.executive} bullets={finInsights.bullets} />
          </div>

          <div className={`${COLORS.card} p-5`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-orange-600" />
                  Schedule & CPI risk
                </h2>
                <p className="text-xs text-gray-500 mt-1">CPI / SPI trend (last ~12 months)</p>
              </div>
              <span className="text-orange-700 text-sm font-mono">{data.health.schedule}/100</span>
            </div>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={perfChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 10 }} />
                  <YAxis domain={[0.4, 1.25]} tick={{ fill: '#6b7280', fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }}
                  />
                  <ReferenceLine y={1} stroke="#f87171" strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="cpi" name="CPI" stroke="#2dd4bf" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="spi" name="SPI" stroke="#fb923c" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2 border-t border-gray-200 pt-4">
              <p className="text-[11px] text-gray-500 uppercase font-semibold">Latest trade CPI</p>
              <div className="flex flex-wrap gap-3">
                {data.trades.map((t) => (
                  <div key={t.name} className="flex-1 min-w-[100px]">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>{t.name}</span>
                      <span className={t.cpi >= 1 ? 'text-teal-700' : 'text-red-600'}>{t.cpi}</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${t.cpi >= 1 ? 'bg-teal-500' : 'bg-red-500'}`}
                        style={{ width: `${clampPct(t.cpi)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] border-t border-gray-200 pt-3">
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5 text-center">
                <p className="text-gray-500">Latest CPI</p>
                <p className="text-teal-800 font-mono">{scheduleSpotlight.latestEv?.cpi ?? '—'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5 text-center">
                <p className="text-gray-500">Latest SPI</p>
                <p className="text-orange-700 font-mono">{scheduleSpotlight.latestEv?.spi ?? '—'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5 text-center">
                <p className="text-gray-500">6-mo avg SPI</p>
                <p className="text-gray-900 font-mono">
                  {scheduleSpotlight.avgSpi6 != null ? scheduleSpotlight.avgSpi6.toFixed(3) : '—'}
                </p>
              </div>
            </div>
            <InsightStrip title="Schedule & EV insights" executive={scheduleInsights.executive} bullets={scheduleInsights.bullets} />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className={`${COLORS.card} p-5`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <HardHat className="w-5 h-5 text-purple-600" />
                  Operations maturity
                </h2>
                <p className="text-xs text-gray-500 mt-1">Merged entity counts · coordination intensity index</p>
              </div>
              <span className="text-purple-700 text-sm font-mono">{data.health.operations}/100</span>
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={opsChartData} margin={{ left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                    <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" width={108} tick={{ fill: '#6b7280', fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} />
                    <Bar dataKey="value" fill="#a78bfa" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="h-44">
                <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 font-semibold">
                  Coordination intensity (index)
                </p>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={opsDocTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 9 }} interval={1} />
                    <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} />
                    <Line type="monotone" dataKey="docIndex" stroke="#c084fc" strokeWidth={2} dot={false} name="Index" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5 text-center">
                <p className="text-gray-500">RFIs merged</p>
                <p className="text-gray-900 font-mono text-sm">{data.ops.rfiMerged}</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5 text-center">
                <p className="text-gray-500">Submittals</p>
                <p className="text-gray-900 font-mono text-sm">{data.ops.submittalsMerged}</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5 text-center">
                <p className="text-gray-500">Change orders</p>
                <p className="text-gray-900 font-mono text-sm">{data.ops.changeOrdersMerged}</p>
              </div>
            </div>
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
              {data.ops.rawNote}
            </p>
            <InsightStrip title="Operations insights" executive={opsInsights.executive} bullets={opsInsights.bullets} />
          </div>

          <div className={`${COLORS.card} p-5`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-teal-700" />
                  Safety health
                </h2>
                <p className="text-xs text-gray-500 mt-1">Fulcrum-style twin · TRIR approx · SOAR culture</p>
              </div>
              <span className="text-teal-800 text-sm font-mono">{data.safety.healthScore}/100</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="h-44 w-full sm:w-1/2">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={safetyPie} dataKey="value" innerRadius={48} outerRadius={72} paddingAngle={2}>
                      {safetyPie.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2 text-sm">
                <p className="text-3xl font-bold text-gray-900">{data.safety.soar.toLocaleString()}</p>
                <p className="text-gray-600 text-xs">Safety observations (SOAR) — daily twin drift included</p>
                <ul className="text-xs text-gray-700 space-y-1 mt-3 font-mono">
                  <li>OSHA recordable: {data.safety.oshaRecordable}</li>
                  <li>First aid only: {data.safety.firstAidOnly}</li>
                  <li>Lost time days: {data.safety.lostTimeDays}</li>
                  <li>TRIR (approx): {data.safety.trir}</li>
                  <li>Man-hours: {data.safety.manHours.toLocaleString()}</li>
                  <li>Days since incident: {data.safety.daysSinceIncident}</li>
                </ul>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] border-t border-gray-200 pt-3">
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">TRIR (approx)</p>
                <p className={`font-mono ${data.safety.trir > 3.5 ? 'text-amber-800' : 'text-teal-800'}`}>
                  {data.safety.trir}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">Man-hours</p>
                <p className="text-gray-900 font-mono">{data.safety.manHours.toLocaleString()}</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">Lost-time days</p>
                <p className={`font-mono ${data.safety.lostTimeDays > 0 ? 'text-red-600' : 'text-teal-800'}`}>
                  {data.safety.lostTimeDays}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-2 py-1.5">
                <p className="text-gray-500">Days since inc.</p>
                <p className="text-gray-900 font-mono">{data.safety.daysSinceIncident}</p>
              </div>
            </div>
            <InsightStrip title="Safety insights" executive={safetyInsights.executive} bullets={safetyInsights.bullets} />
          </div>
        </div>
          </>
        )}

        {mainTab === 'qoq' && (
        <div className={`${COLORS.card} p-5`}>
          {quarters.length < 2 && (
            <p className="text-sm text-amber-900 mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              Not enough history for quarter rolls-ups at this as-of date (often before job start). Move the calendar
              forward or pick another project.
            </p>
          )}
          <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-cyan-700" />
                Quarter-to-quarter progress
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Actual twin months through <span className="text-gray-700">{data.dataAsOf}</span>
                {data.forecastMonthsCount > 0 ? (
                  <>
                    {' '}
                    · forward projection through{' '}
                    <span className="text-violet-800">{data.twinForecastThroughYm}</span> ({data.forecastMonthsCount}{' '}
                    mo) highlights <span className="text-violet-800">2026 Q3–Q4 &amp; 2027</span> for prediction-style
                    QoQ.
                  </>
                ) : null}{' '}
                Baseline → compare follow calendar order.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 xl:justify-end">
              <select
                value={qBase}
                onChange={(e) => setQBase(e.target.value)}
                className="bg-white border border-gray-300 text-gray-900 rounded-lg px-2 py-1.5 text-sm min-w-[10rem]"
              >
                {quarters.map((q) => (
                  <option key={q.quarterKey} value={q.quarterKey}>
                    {q.quarterKey}
                    {quarterBlendTag(q.blend)} (baseline)
                  </option>
                ))}
              </select>
              <ChevronRight className="w-4 h-4 text-gray-500 hidden sm:block self-center" />
              <select
                value={qCompare}
                onChange={(e) => setQCompare(e.target.value)}
                className="bg-white border border-gray-300 text-gray-900 rounded-lg px-2 py-1.5 text-sm min-w-[10rem]"
              >
                {quarters.map((q) => (
                  <option key={`c-${q.quarterKey}`} value={q.quarterKey}>
                    {q.quarterKey}
                    {quarterBlendTag(q.blend)} (compare)
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-3">
            {[
              { id: 'insights', label: 'Insights & summary', Icon: Sparkles },
              { id: 'dashboard', label: 'Comparison dashboard', Icon: LayoutGrid },
              { id: 'table', label: 'All quarters table', Icon: Table2 },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setQoqTab(id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  qoqTab === id
                    ? 'bg-teal-100 border-teal-600 text-teal-900 font-semibold shadow-sm'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          {qoqTab === 'insights' && comparison?.ok && (
            <div className="space-y-6">
              <QoQInsightsRich insights={comparison.insights} />
              <details className="rounded-xl border border-gray-200 bg-gray-50">
                <summary className="px-4 py-3 text-xs font-medium text-gray-600 cursor-pointer hover:text-gray-800">
                  Figure appendix — numeric line items
                </summary>
                <pre className="px-4 pb-4 text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed border-t border-gray-200 pt-3">
                  {comparison.narrative.replace(/\*\*/g, '')}
                </pre>
              </details>
            </div>
          )}

          {qoqTab === 'insights' && !comparison?.ok && quarters.length >= 2 && (
            <p className="text-sm text-gray-500">Pick two different quarters to generate an insights summary.</p>
          )}

          {qoqTab === 'dashboard' && qoqDashboard && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                Side-by-side view for{' '}
                <span className="text-teal-800 font-mono">{qoqDashboard.baseline.quarterKey}</span>{' '}
                <span className="text-gray-500">({qoqDashboard.baseline.blend})</span> vs{' '}
                <span className="text-teal-800 font-mono">{qoqDashboard.compare.quarterKey}</span>{' '}
                <span className="text-gray-500">({qoqDashboard.compare.blend})</span>. Actual quarters use neutral gray / teal;
                projected / mixed quarters shift toward violet or amber.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-2">
                {[
                  { label: 'Quarter burn', a: `$${qoqDashboard.baseline.costM}M`, b: `$${qoqDashboard.compare.costM}M` },
                  { label: 'Avg / mo burn', a: `$${qoqDashboard.baseline.avgMonthlyBurn}M`, b: `$${qoqDashboard.compare.avgMonthlyBurn}M` },
                  { label: 'Billings', a: `$${qoqDashboard.baseline.billedQuarterM}M`, b: `$${qoqDashboard.compare.billedQuarterM}M` },
                  { label: 'WIP end', a: `$${qoqDashboard.baseline.endWipM}M`, b: `$${qoqDashboard.compare.endWipM}M` },
                  { label: 'Burn σ', a: `$${qoqDashboard.baseline.costVolatility}M`, b: `$${qoqDashboard.compare.costVolatility}M` },
                  {
                    label: '$ / pt % in-Q',
                    a: qoqDashboard.baseline.burnPerPctPt != null ? `$${qoqDashboard.baseline.burnPerPctPt}M` : '—',
                    b: qoqDashboard.compare.burnPerPctPt != null ? `$${qoqDashboard.compare.burnPerPctPt}M` : '—',
                  },
                ].map((row) => (
                  <div key={row.label} className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 text-[10px]">
                    <p className="text-gray-500 uppercase tracking-wide font-semibold">{row.label}</p>
                    <p className="text-gray-600 mt-1">
                      <span className="text-gray-500">Base:</span> <span className="text-gray-800 font-mono">{row.a}</span>
                    </p>
                    <p className="text-gray-600">
                      <span className="text-gray-500">Cmp:</span> <span className="text-teal-800 font-mono">{row.b}</span>
                    </p>
                  </div>
                ))}
              </div>

              <div className="grid lg:grid-cols-2 gap-5">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Quarter burn ($M)</p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={qoqDashboard.costData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="quarter" tick={{ fill: '#6b7280', fontSize: 10 }} interval={0} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip
                          contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }}
                          formatter={(v) => [`$${v}M`, 'Cost']}
                        />
                        <Bar dataKey="costM" name="Cost ($M)" radius={[6, 6, 0, 0]}>
                          {qoqDashboard.costData.map((entry, i) => (
                            <Cell key={i} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    Billings recognized ($M)
                  </p>
                  <p className="text-[11px] text-gray-500 mb-2">Share of revised contract: baseline {qoqDashboard.baseline.billedPctContract}% · compare {qoqDashboard.compare.billedPctContract}%</p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={qoqDashboard.billedData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="quarter" tick={{ fill: '#6b7280', fontSize: 10 }} interval={0} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} />
                        <Bar dataKey="billedM" name="Billed ($M)" radius={[6, 6, 0, 0]}>
                          {qoqDashboard.billedData.map((entry, i) => (
                            <Cell key={i} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    Earned value indices (avg)
                  </p>
                  <p className="text-[11px] text-gray-500 mb-2">1.0 = on target. CPI = cost efficiency; SPI = schedule.</p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={qoqDashboard.perfData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="quarter" tick={{ fill: '#6b7280', fontSize: 10 }} interval={0} />
                        <YAxis domain={[0.35, 1.25]} tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} />
                        <Legend wrapperStyle={{ fontSize: 11, color: '#374151' }} />
                        <ReferenceLine y={1} stroke="#f87171" strokeDasharray="4 4" />
                        <Bar dataKey="CPI" fill="#2dd4bf" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="SPI" fill="#fb923c" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    Monthly burn volatility (σ)
                  </p>
                  <p className="text-[11px] text-gray-500 mb-2">Std. dev. of month-over-month job cost inside each quarter.</p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={qoqDashboard.volatilityData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="quarter" tick={{ fill: '#6b7280', fontSize: 10 }} interval={0} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} formatter={(v) => [`$${v}M`, 'σ']} />
                        <Bar dataKey="sigma" fill="#fbbf24" name="σ ($M)" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    Physical progress (% complete @ quarter-end)
                  </p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={qoqDashboard.progressData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="quarter" tick={{ fill: '#6b7280', fontSize: 10 }} interval={0} />
                        <YAxis domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip
                          contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }}
                          formatter={(v) => [`${v}%`, 'Complete']}
                        />
                        <Bar dataKey="pctComplete" fill="#818cf8" name="% Complete" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    WIP proxy ($M @ quarter-end)
                  </p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={qoqDashboard.wipData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="quarter" tick={{ fill: '#6b7280', fontSize: 10 }} interval={0} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} formatter={(v) => [`$${v}M`, 'WIP']} />
                        <Bar dataKey="wipM" fill="#94a3b8" name="WIP" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    Composite health hint (model score)
                  </p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={qoqDashboard.healthData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="quarter" tick={{ fill: '#6b7280', fontSize: 10 }} interval={0} />
                        <YAxis domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} />
                        <Bar dataKey="score" fill="#34d399" name="Health hint" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    Burn per point % complete (in-quarter)
                  </p>
                  <p className="text-[11px] text-gray-500 mb-2">Total quarter burn ÷ % complete earned inside the quarter.</p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={qoqDashboard.burnEfficiencyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="quarter" tick={{ fill: '#6b7280', fontSize: 10 }} interval={0} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} formatter={(v) => [`$${v}M`, 'Per pt']} />
                        <Bar dataKey="burnPerPct" fill="#14b8a6" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 lg:col-span-2">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    Intra-quarter burn profile ($M by month slot)
                  </p>
                  <p className="text-[11px] text-gray-500 mb-2">
                    Aligns month 1…n across partial quarters so you can see pacing vs the prior comparison period.
                  </p>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={qoqDashboard.burnProfile}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="slot" tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} />
                        <Legend wrapperStyle={{ fontSize: 11, color: '#374151' }} />
                        <Bar dataKey="baseline" fill="#64748b" name="Baseline Q" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="compare" fill="#2dd4bf" name="Compare Q" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 lg:col-span-2">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    Document-control throughput (merged est.)
                  </p>
                  <p className="text-[11px] text-gray-500 mb-2">Quarter-level RFIs, submittals, and CO actions inferred from burn intensity.</p>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={qoqDashboard.opsDocsData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="quarter" tick={{ fill: '#6b7280', fontSize: 10 }} interval={0} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} />
                        <Legend wrapperStyle={{ fontSize: 11, color: '#374151' }} />
                        <Bar dataKey="RFIs" fill="#a78bfa" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Submittals" fill="#818cf8" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="COs" fill="#f472b6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 lg:col-span-2">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    Safety culture pulse (quarter deltas)
                  </p>
                  <p className="text-[11px] text-gray-500 mb-2">
                    Observations reflect proactive reporting; incidents are trailing indicators for the same window.
                  </p>
                  <div className="h-56 max-w-2xl mx-auto">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={qoqDashboard.safetyCultureData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="quarter" tick={{ fill: '#6b7280', fontSize: 10 }} interval={0} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111827' }} />
                        <Legend wrapperStyle={{ fontSize: 11, color: '#374151' }} />
                        <Bar dataKey="observations" fill="#38bdf8" name="SOAR Δ" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="incidents" fill="#f472b6" name="Incidents Δ" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {comparison?.ok && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Dashboard caption</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{comparison.insights.executiveSummary}</p>
                </div>
              )}
            </div>
          )}

          {qoqTab === 'dashboard' && !qoqDashboard && quarters.length >= 2 && (
            <p className="text-sm text-gray-500">Select two distinct quarters to render the comparison dashboard.</p>
          )}

          {qoqTab === 'table' && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-3 whitespace-nowrap">Quarter</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Kind</th>
                    <th className="py-2 pr-3">Mo</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Burn Q ($M)</th>
                    <th className="py-2 pr-3 whitespace-nowrap">$/mo</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Bill Q ($M)</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Bill % rev</th>
                    <th className="py-2 pr-3 whitespace-nowrap">WIP end</th>
                    <th className="py-2 pr-3 whitespace-nowrap">σ burn</th>
                    <th className="py-2 pr-3">CPI</th>
                    <th className="py-2 pr-3">SPI</th>
                    <th className="py-2 pr-3 whitespace-nowrap">% done</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Δ% in Q</th>
                    <th className="py-2 pr-3 whitespace-nowrap">$/pt%</th>
                    <th className="py-2 pr-3 whitespace-nowrap">RFI Q</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Sub Q</th>
                    <th className="py-2 pr-3 whitespace-nowrap">CO Q</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Inc Δ</th>
                    <th className="py-2 pr-3 whitespace-nowrap">Obs Δ</th>
                    <th className="py-2 whitespace-nowrap">Health</th>
                  </tr>
                </thead>
                <tbody className="text-gray-800 font-mono text-[10px]">
                  {quarters.map((row) => (
                    <tr
                      key={row.quarterKey}
                      className={`border-b border-gray-200/80 ${row.blend === 'projected' ? 'bg-violet-50' : row.blend === 'mixed' ? 'bg-amber-50' : ''}`}
                    >
                      <td className="py-2 pr-3 text-teal-800 whitespace-nowrap">{row.quarterKey}</td>
                      <td className="py-2 pr-3 text-[9px] uppercase tracking-wide text-gray-600 whitespace-nowrap">
                        {row.blend === 'projected'
                          ? 'Projected'
                          : row.blend === 'mixed'
                            ? 'Mixed'
                            : 'Actual'}
                      </td>
                      <td className="py-2 pr-3">{row.monthsIncluded}</td>
                      <td className="py-2 pr-3">{row.costM}</td>
                      <td className="py-2 pr-3">{row.avgMonthlyBurn}</td>
                      <td className="py-2 pr-3">{row.billedQuarterM}</td>
                      <td className="py-2 pr-3">{row.billedPctContract}%</td>
                      <td className="py-2 pr-3">{row.endWipM}</td>
                      <td className="py-2 pr-3">{row.costVolatility}</td>
                      <td className="py-2 pr-3">{row.avgCpi}</td>
                      <td className="py-2 pr-3">{row.avgSpi}</td>
                      <td className="py-2 pr-3">{row.endPctComplete}%</td>
                      <td className="py-2 pr-3">{row.deltaPctInQuarter}%</td>
                      <td className="py-2 pr-3">{row.burnPerPctPt ?? '—'}</td>
                      <td className="py-2 pr-3">{row.rfiQuarter}</td>
                      <td className="py-2 pr-3">{row.submittalsQuarter}</td>
                      <td className="py-2 pr-3">{row.changeOrdersQuarter}</td>
                      <td className="py-2 pr-3">{row.incidentDelta}</td>
                      <td className="py-2 pr-3">{row.obsDelta}</td>
                      <td className="py-2">{row.compositeHint}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {mainTab === 'nl' && canAccessDigitalTwinDemo(user?.email) && (
          <div className={`${COLORS.card} p-5`}>
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Bot className="w-5 h-5 text-violet-700" />
                  Agentic dashboard from your query
                </h2>
                <p className="text-xs text-gray-500 mt-1 max-w-2xl leading-relaxed">
                  Describe the twin view you want in plain language. The Jobotics planner reads the same demo snapshot as
                  this page (including projected 2026 Q3–Q4 &amp; 2027 quarters) and returns a small chart layout — no
                  manual widget picking.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {SAMPLE_TWIN_AGENT_QUERIES.slice(0, 4).map((sq) => (
                <button
                  key={sq}
                  type="button"
                  onClick={() => {
                    setAgentQuery(sq);
                    setAgentError('');
                  }}
                  className="text-left text-[11px] px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:border-violet-400 hover:text-violet-800 transition-colors max-w-[14rem] leading-snug"
                >
                  {sq}
                </button>
              ))}
              <button
                type="button"
                onClick={surpriseTwinQuery}
                className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg border border-violet-300 text-violet-800 hover:bg-violet-50 transition-colors"
              >
                <Shuffle className="w-3.5 h-3.5" />
                Random query
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <label className="flex-1 block">
                <span className="sr-only">Dashboard query</span>
                <textarea
                  value={agentQuery}
                  onChange={(e) => setAgentQuery(e.target.value)}
                  rows={3}
                  placeholder="e.g. Compare projected 2026 Q3 vs Q4 cost and billings, add CPI trend for the last year…"
                  className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 resize-y min-h-[5rem]"
                />
              </label>
              <button
                type="button"
                onClick={runTwinDashboardAgent}
                disabled={agentLoading}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-45 text-white text-sm font-medium shrink-0"
              >
                {agentLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Generate dashboard
              </button>
            </div>

            {agentError ? (
              <p className="mt-3 text-sm text-amber-900 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                {agentError}
              </p>
            ) : null}

            {agentPlan && agentSnapshot ? (
              <div className="mt-6 border-t border-gray-200 pt-5">
                <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 mb-4">
                  <p className="text-lg font-semibold text-gray-900">{agentPlan.dashboardTitle}</p>
                  {agentPlan.dashboardSubtitle ? (
                    <p className="text-sm text-gray-600 mt-1">{agentPlan.dashboardSubtitle}</p>
                  ) : null}
                  {agentPlan.narrative ? (
                    <p className="text-sm text-gray-700 mt-3 leading-relaxed">{agentPlan.narrative}</p>
                  ) : null}
                  {agentModel ? (
                    <p className="text-[10px] text-gray-500 mt-2 font-mono">Planner model: {agentModel}</p>
                  ) : null}
                </div>
                <TwinAgentWidgets plan={agentPlan} snapshot={agentSnapshot} />
              </div>
            ) : null}
          </div>
        )}

        {mainTab === 'kg' && canAccessDigitalTwinDemo(user?.email) && <KnowledgeGraphPanel user={user} />}

        <footer className="flex flex-wrap items-center justify-between gap-3 text-[11px] text-gray-500 border-t border-gray-200 pt-4">
          <span>
            Data as of <span className="text-gray-700">{data.dataAsOf}</span> · Sources: {data.sources.join(', ')}
            {data.forecastMonthsCount > 0 ? (
              <>
                {' '}
                · QoQ includes{' '}
                <span className="text-violet-800/90">
                  twin-forward projection through {data.twinForecastThroughYm}
                </span>{' '}
                ({data.forecastMonthsCount} mo past as-of).
              </>
            ) : null}
          </span>
          <span className="text-amber-800">
            Twin rule: metrics are deterministic per project + calendar day (advance date for fresh deltas).
          </span>
        </footer>
      </div>
    </div>
  );
}

function clampPct(cpi) {
  return Math.round(Math.min(100, Math.max(8, cpi * 70)));
}
